import { ApiError } from '@/lib/api';
import type { EmailThreadMatchStatus } from '@/lib/types';

/*
 * Shared wording and helpers for the Email Log screens (`/email-log`, the thread
 * modal, the assign dialog). Kept in a `.ts` file so the component files export
 * components only (fast refresh).
 */

export const DENIED = 'Your permission set does not include this. An administrator can change that.';

export const MATCH_STATUS_LABELS: Record<EmailThreadMatchStatus, string> = {
  unmatched: 'Unmatched',
  ambiguous: 'Ambiguous',
  matched: 'Logged',
  ignored: 'Ignored',
};

export const MATCH_STATUS_TONES: Record<
  EmailThreadMatchStatus,
  'neutral' | 'warning' | 'success' | 'info'
> = {
  unmatched: 'warning',
  ambiguous: 'info',
  matched: 'success',
  ignored: 'neutral',
};

/** "Name <address>" when a display name exists, else the address. */
export function addressLabel(addr: { address: string; name: string | null }): string {
  const name = addr.name?.trim();
  return name ? name : addr.address;
}

/** Whole bytes → "12 KB" / "3.4 MB". `null` → em dash. */
export function formatBytes(size: number | null): string {
  if (size === null) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const SKIPPED_REASON_LABELS: Record<string, string> = {
  too_large: 'Not saved: over 25 MB',
  inline: 'Not saved: inline image',
  upload_failed: 'Not saved: upload failed',
};

export function skippedReasonLabel(reason: string): string {
  return SKIPPED_REASON_LABELS[reason] ?? `Not saved: ${reason.replace(/_/g, ' ')}`;
}

/*
 * 🔴 An email body is untrusted HTML. It renders only inside an
 * `<iframe sandbox="">` (no scripts, no same-origin, no popups) whose CSP loads
 * nothing from the network — the same rule as `AttachmentViewer`'s sandbox.
 */
const SANDBOX_CSP = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

export function sandboxEmailDocument(body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}"><style>body{font:13px/1.5 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1a1a;background:#fff;margin:12px;word-wrap:break-word;overflow-wrap:anywhere}img{max-width:100%;height:auto}pre,tt{white-space:pre-wrap}</style></head><body>${body}</body></html>`;
}

/** A triage refusal → one sentence, matched on `error.code` first. */
export function triageErrorMessage(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'COMPANY_NOT_FOUND':
        return 'That company no longer exists. Choose another.';
      case 'EMAIL_THREAD_ALREADY_MATCHED':
        return 'This thread is already logged against a company.';
      case 'NOT_FOUND':
        return 'This thread no longer exists.';
      default:
        break;
    }
    if (caught.status === 403) return DENIED;
    if (caught.status === 404) return 'This thread no longer exists.';
    const form = caught.formMessages[0];
    if (form) return form;
    return caught.message || fallback;
  }
  return caught instanceof Error ? caught.message : fallback;
}
